// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";

// 3. API & Types
import { deleteFleetVehicleFireExtinguisherAPI, getFleetVehicleFireExtinguishersAPI } from "services/api/fleet/vehicles/fireextinguishers/api";
import {
  FleetVehicleFireExtinguisherRow,
} from "services/api/fleet/vehicles/fireextinguishers/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// 4. UI Components
import { TableSearch } from "ui/components/Table/TableSearch";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";

// 5. Local Components & Constants
import VehicleFireExtinguisherEditCard from "../../../VehicleFireExtinguisherEditCard/VehicleFireExtinguisherEditCard";
import { FLEET_VEHICLE_EDIT_FIRE_EXTINGUISHER_SEARCH_COLUMNS } from "./consts";

// 6. Local Types
import { VehicleFireExtinguisherFormProps } from "./types";
import { Loader } from "ui/components/Loader";

const VehicleFireExtinguisherForm: React.FC<
  VehicleFireExtinguisherFormProps
> = (props) => {
  const { formValues, setFormValues, setVehicleInfo } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleFireExtinguisherRow>
  >(initialDataWithPagination);

  const [fireExtinguisherId, setFireExtinguisherId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (formValues.id)
      fetchSearchResult(formValues.id, searchResults.pagination);
  }, [formValues.id]);

  const fetchSearchResult = async (id: string, pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleFireExtinguishersAPI(id, pagination);
      setSearchResults(results);

      // Ažuriraj summary s podacima prvog vatrogasnog aparata
      if (results.data.length > 0) {
        const first = results.data[0];
        setVehicleInfo(prev => ({
          ...prev,
          fireExtinguisherName: first.name ?? null,
          fireExtinguisherExpirationDate: first.expirationDate
            ? new Date(first.expirationDate).toLocaleDateString("hr-HR")
            : null,
          fireExtinguisherPrice: first.price ?? null,
          fireExtinguisherWeight: first.weight ?? null,
          fireExtinguisherSerialNumber: first.serialNumber ?? null,
        }));
      }
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setFireExtinguisherId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setFireExtinguisherId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setFireExtinguisherId(id);
      setOpenDialog(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    if (formValues.id)
      fetchSearchResult(formValues.id, searchResults.pagination);

    setFireExtinguisherId("");
    setShowAddNewModal(false);
  }, []);

  const handleFireExtinguisherDeleteDialogConfirm = useCallback(async () => {
    if (!fireExtinguisherId) return;

    try {
      const deleted = await deleteFleetVehicleFireExtinguisherAPI(
        fireExtinguisherId,
        t
      );

      if (deleted && formValues.id) {
        await fetchSearchResult(formValues.id, searchResults.pagination);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setFireExtinguisherId("");
    } catch (err) {
      console.error("Greška prilikom brisanja dokumenta:", err);
    }
  }, [fireExtinguisherId, t, formValues.id, searchResults.pagination]);

  const handleFireExtinguisherDeleteDialogClose = useCallback(() => {
    setFireExtinguisherId("");
    setOpenDialog(false);
  }, []);

  if (!formValues.id) return <></>;


  if (isLoading) {
    return (
      <div className="relative flex items-center justify-center px-4 h-[calc(100vh-150px)]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  return (
    <>
      <div className="relative flex justify-center px-4 pt-12  h-[calc(100vh-150px)]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">{t("admin.fleet.vehicle.edit.fireextinguisher.title")}</h1>
          <img src="/media/images/vehicles/fire-extinguisher.webp" alt="" className="mx-auto h-[150px]" />
          <p className="text-center text-md text-slate-600 mb-4 max-w-2xl mx-auto ">Dodajte vatrogasni aparat na vozilo klikom na gumb <strong>"Dodaj vatrogasni aparat"</strong> kako biste nastavili s konfiguracijom. Nakon što ste dodali gume možete nastaviti dalje klikom na gumb "Nastavi</p>
          <div className="px-3">
            {!isLoading && searchResults.pagination.totalPages === 0 ? (
              <>
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    children={t("admin.vehicle.fireextinguisher.search.add")}
                    onClick={handleAddNew}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="">
                  <TableSearch
                    columns={FLEET_VEHICLE_EDIT_FIRE_EXTINGUISHER_SEARCH_COLUMNS(
                      t,
                      handleDeleteClick
                    )}
                    data={searchResults.data}
                    selectableRows={false}
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                  />
                </div>
              </>

            )}
          </div>
        </div>
      </div>

      <VehicleFireExtinguisherEditCard
        show={showAddNewModal}
        vehicleId={formValues.id}
        fireExtinguisherId={fireExtinguisherId}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />

      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.edit.fireextinguisher.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.edit.fireextinguisher.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.edit.fireextinguisher.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.edit.fireextinguisher.dialog.delete.canceltext"
        )}
        onConfirm={handleFireExtinguisherDeleteDialogConfirm}
        onCancel={handleFireExtinguisherDeleteDialogClose}
      />
    </>
  );
};
export default memo(VehicleFireExtinguisherForm);
